import React from 'react';
import { Card, Chip, Divider, Stack, Typography } from '@mui/material';

import ListaDeIndicadores from './componentes/ListaDeIndicadores';
import Colunas from './componentes/Colunas';
import BlocoBloqueado from './componentes/BlocoBloqueado';
import CoberturaChip from '../components/CoberturaChip';
import { ABAS } from './abas';
import { linhasDoPainel } from './apresentacao';
import { PERFIL_DE_FAIXAS, VERSAO_DAS_FAIXAS } from '../diagnostico/faixas';
import TituloDaTela from './componentes/TituloDaTela';
import { LARGURA_DE_LEITURA } from './layout';

/**
 * Painel de Indicadores — a tela densa, feita para quem trabalha o dado.
 *
 * As telas de leitura são arejadas, uma ideia por dobra, para o diretor ler no
 * celular. Esta é o oposto de propósito: todo indicador do catálogo, agrupado
 * por seção, em linha compacta. É a tela que o consultor abre quando já sabe o
 * que procura.
 *
 * O que ela NÃO faz: não repete a mesma régua com outro nome. A faixa de cada
 * número é a mesma das telas de leitura, resolvida pelo mesmo catálogo — e a
 * versão da régua e o perfil aplicado ficam declarados no rodapé, porque o
 * consultor precisa poder discordar da faixa com argumento.
 */
/** Quantos indicadores da seção estão em cada faixa. */
const contarPorFaixa = (itens = []) => ({
  criticos: itens.filter((i) => i.nivel === 'critico').length,
  atencao: itens.filter((i) => i.nivel === 'atencao').length
});

const TelaPainel = ({ leitura, onIrPara }) => {
  const secoes = linhasDoPainel(leitura);
  const irParaDados = () => onIrPara && onIrPara(ABAS.DADOS);

  return (
    <Stack spacing={3} sx={{ maxWidth: LARGURA_DE_LEITURA, mx: 'auto' }}>
      <TituloDaTela
        titulo="Painel de Indicadores"
        descricao="Todos os indicadores calculados, agrupados pela dependência de dado."
        acao={<CoberturaChip cobertura={leitura?.cobertura} />}
      />

      {/* AS SEIS SEÇÕES EM DUAS COLUNAS a partir de 1200px.
          Empilhadas, elas somavam 5.600px de rolagem numa tela que sobrava um
          terço de largura vazio dos dois lados — e esta é justamente a tela
          densa do produto, aquela que o consultor abre quando já sabe o que
          procura. Duas colunas cortam a rolagem pela metade e põem mais
          indicador por dobra, que é o que a régua de terminal pede. */}
      <Colunas apartir="lg" minimo={440} sx={{ alignItems: 'start' }}>
      {secoes.map((secao) =>
        secao.aberta ? (
          <Card key={secao.secao} variant="outlined" sx={{ p: { xs: 2, sm: 3 } }}>
            {/* O CABEÇALHO DIZ QUANTOS ESTÃO RUINS, e não só quantos existem.
                Seis cartões idênticos com 36 linhas de mesmo peso davam ao dono
                uma lista onde a promessa é "saber o que atacar primeiro". A
                contagem por faixa é a menor coisa que devolve hierarquia sem
                desmontar o agrupamento por dependência de dado, que é o que
                esta tela existe para mostrar. */}
            <Stack direction="row" spacing={1} alignItems="center" flexWrap="wrap" useFlexGap sx={{ mb: 1 }}>
              <Typography variant="h5" component="h2">
                {secao.titulo}
              </Typography>
              <Chip size="small" variant="outlined" label={`${secao.itens.length}`} />
              {contarPorFaixa(secao.itens).criticos > 0 && (
                <Chip
                  size="small"
                  color="error"
                  variant="outlined"
                  label={`${contarPorFaixa(secao.itens).criticos} crítico${contarPorFaixa(secao.itens).criticos > 1 ? 's' : ''}`}
                />
              )}
              {contarPorFaixa(secao.itens).atencao > 0 && (
                <Chip
                  size="small"
                  color="warning"
                  variant="outlined"
                  label={`${contarPorFaixa(secao.itens).atencao} em atenção`}
                />
              )}
            </Stack>
            <Divider sx={{ mb: 1 }} />
            <ListaDeIndicadores itens={secao.itens} />
          </Card>
        ) : (
          <BlocoBloqueado
            key={secao.secao}
            titulo={secao.titulo}
            chamada={secao.estado.chamada}
            campos={secao.estado.campos}
            bloqueados={secao.estado.bloqueados}
            onDestravar={irParaDados}
          />
        )
      )}

      </Colunas>

      <Typography variant="caption" color="text.secondary" sx={{ px: 1, maxWidth: '75ch' }}>
        Faixas de saúde: {PERFIL_DE_FAIXAS.nome} ({VERSAO_DAS_FAIXAS}). {PERFIL_DE_FAIXAS.aviso} —
        não há base estatística setorial própria, e um perfil setorial inventado classificaria
        errado justamente quem opera na média do próprio setor.
      </Typography>
    </Stack>
  );
};

export default TelaPainel;
